import { makeAutoObservable } from 'mobx';
import React from 'react';
import { AccountInterface } from 'type/accountType';
import { accountApiService } from '../service/api/accountApiService';
import {
  ADMIN_MENU_ITEMS,
  CONTENT_MENU_ITEMS,
  CONTRACT_MENU_ITEMS,
  CP_MENU_KEY_LIST,
  INGEST_MENU_ITEMS,
  META_DATA_STRUCTURE_MENU_ITEMS,
  PERMISSION_MENU_ITEMS,
  REVENUE_MENU_ITEMS,
  SYNC_MENU_ITEMS,
  TOOLS_MENU_ITEMS,
} from 'constants/menu';

// user_id === '1' 은 슈퍼 어드민으로, 모든 권한/메뉴 체크를 통과한다.
export const SUPER_ADMIN_USER_ID = '1';

const collectMenuKeys = (items: { key?: React.Key }[] | undefined): string[] =>
  (items ?? [])
    .map(item => (typeof item?.key === 'string' ? item.key : ''))
    .filter((k): k is string => !!k);

// 슈퍼 어드민에게 노출할 전체 메뉴 키. (master 에는 VENDOR_MENU_ITEMS 가 없어 제외)
const ALL_MENU_KEYS: string[] = [
  ...collectMenuKeys(CONTENT_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(META_DATA_STRUCTURE_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(CONTRACT_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(REVENUE_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(INGEST_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(SYNC_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(TOOLS_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(ADMIN_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(PERMISSION_MENU_ITEMS as { key?: React.Key }[] | undefined),
];

class AccountStore {
  private account: AccountInterface | null = null;
  private projectId: number | null = null;
  private role: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  public reset = (): void => {
    this.account = null;
    this.projectId = null;
    this.role = null;
  };

  public async init(): Promise<void> {
    const account = await accountApiService.fetchAccount();
    this.account = account;
    const { project_id: projectId, role } = await accountApiService.getUserProjectRoles(
      Number(this.account?.id)
    );
    this.projectId = projectId;
    this.role = role;
  }

  public async fetchAccount(): Promise<AccountInterface> {
    if (this.account) {
      return this.account;
    }
    const account = await accountApiService.fetchAccount();
    const { project_id: projectId, role } = await accountApiService.getUserProjectRoles(
      Number(account.id)
    );
    this.projectId = projectId;
    this.role = role;
    this.account = account;
    return account;
  }

  public async update(account: AccountInterface): Promise<void> {
    await accountApiService.update(Number(account.id), account);
  }

  public async updatePassword(oldPassword: string, newPassword: string): Promise<void> {
    await accountApiService.updatePassword(oldPassword, newPassword);
  }

  public setAccount = (account: AccountInterface): void => {
    this.account = account;
  };

  public setProject = (projectId: number): void => {
    if (this.projectId) {
      this.projectId = projectId;
    } else {
      throw new Error('Account not found');
    }
  };

  public getAccount = (): AccountInterface | null => {
    return this.account;
  };

  public getProject = (): number | null => {
    return this.projectId;
  };

  public getRole = (): string | null => {
    return this.role;
  };

  public isStaff = (): boolean => {
    return this.account?.is_staff ?? false;
  };

  public isInit = (): boolean => {
    return this.account !== null;
  };

  public isSuperAdmin = (): boolean => {
    return String(this.account?.id ?? '') === SUPER_ADMIN_USER_ID;
  };

  public getMenus = (): string[] => {
    // Super admin (user_id=1): 권한과 무관하게 모든 메뉴 노출.
    if (this.isSuperAdmin()) {
      return ALL_MENU_KEYS;
    }

    const permissions = Array.from(this.account?.permissions ?? []);
    const permissionList = permissions.map(permission => permission.split(':')[0]);
    permissionList.push(...INGEST_MENU_ITEMS!.map(item => item!.key as string));
    permissionList.push(...SYNC_MENU_ITEMS!.map(item => item!.key as string));
    return this.account?.platform.id === 2
      ? permissionList.filter(item => CP_MENU_KEY_LIST.includes(item))
      : permissionList;
  };

  public getPermissions = (): string[] => {
    return this.account?.permissions ?? [];
  };

  public hasPermission = (menu: string, action: string): boolean => {
    // Super admin은 모든 권한 통과.
    if (this.isSuperAdmin()) return true;
    const permissions = Array.from(this.account?.permissions ?? []);
    const permission = `${menu}:${action}`;
    return permissions.includes(permission);
  };
}

export const accountStore = new AccountStore();
